import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
  Divider, FormControlLabel, Grid, InputAdornment, Stack, TextField, Tooltip, Typography
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LinkIcon from '@mui/icons-material/Link';
import CheckIcon from '@mui/icons-material/Check';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function PublicApplyForm() {
  const { token } = useParams();

  const [posting, setPosting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('details'); // 'details' | 'form'
  const [copied, setCopied] = useState(false);

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    city: '',
    address: '',
    dateOfBirth: '',
    education: '',
    experience: '',
    skills: '',
    linkedinUrl: '',
    portfolioUrl: '',
    referredBy: '',
    notes: '',
    consent: false,
    // I NDARË nga pëlqimi i mësipërm dhe OPSIONAL: aplikimi nuk varet nga ai.
    reuseConsent: false,
  });
  const [errors, setErrors] = useState({});
  const [documents, setDocuments] = useState([]); // File[] — CV, certifikata (maks 3 × 10MB)

  const addDocuments = (fileList) => {
    const incoming = Array.from(fileList || []);
    const MAX_SIZE = 10 * 1024 * 1024;
    const tooBig = incoming.filter(f => f.size > MAX_SIZE).map(f => f.name);
    const valid = incoming.filter(f => f.size <= MAX_SIZE);
    if (tooBig.length) setError(`Skedarë shumë të mëdhenj (maks 10MB): ${tooBig.join(', ')}`);
    setDocuments(prev => [...prev, ...valid].slice(0, 3));
  };

  useEffect(() => {
    fetch(`${API_URL}/api/public/apply/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data) setPosting(d.data);
        else setNotFound(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = 'Emri i plotë është i detyrueshëm';
    if (!form.email.trim()) errs.email = 'Email-i është i detyrueshëm';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Email invalid';
    if (!form.phone.trim()) errs.phone = 'Telefoni është i detyrueshëm';
    if (!form.consent) errs.consent = 'Duhet të pajtoheni me ruajtjen e të dhënave për të vazhduar.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setError('');
    try {
      // Me dokumente → multipart/form-data; pa to → JSON i thjeshtë si më parë.
      let fetchOpts;
      if (documents.length > 0) {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
        documents.forEach(f => fd.append('documents', f, f.name));
        fetchOpts = { method: 'POST', body: fd };
      } else {
        fetchOpts = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        };
      }
      const res = await fetch(`${API_URL}/api/public/apply/${token}`, fetchOpts);
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        setError(data.error || 'Ndodhi një gabim. Provoni përsëri.');
      }
    } catch {
      setError('Nuk mund të lidhemi me serverin. Kontrolloni lidhjen tuaj.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9fafb' }}>
        <CircularProgress />
      </Box>
    );
  }

  // ─── Not Found ─────────────────────────────────────────────────────────────
  if (notFound) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9fafb', p: 2 }}>
        <Card elevation={2} sx={{ maxWidth: 460, width: '100%', textAlign: 'center', p: 4 }}>
          <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Konkursi nuk u gjet</Typography>
          <Typography color="text.secondary">Linku mund të jetë i gabuar ose konkursi është fshirë.</Typography>
        </Card>
      </Box>
    );
  }

  // ─── Closed / Expired ──────────────────────────────────────────────────────
  if (!posting.isOpen) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9fafb', p: 2 }}>
        <Card elevation={2} sx={{ maxWidth: 500, width: '100%', textAlign: 'center', p: 4 }}>
          <BlockIcon sx={{ fontSize: 56, color: 'warning.main', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {posting.isExpired ? 'Afati i aplikimit ka kaluar'
              : posting.isUpcoming ? 'Aplikimi hapet së shpejti'
              : 'Aplikimi është mbyllur'}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {posting.isExpired
              ? `Afati i fundit ishte ${posting.deadline ? new Date(posting.deadline).toLocaleDateString('sq-AL', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}.`
              : posting.isUpcoming
                ? `Aplikimet pranohen nga ${posting.startDate ? new Date(posting.startDate).toLocaleDateString('sq-AL', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}${posting.deadline ? ` deri më ${new Date(posting.deadline).toLocaleDateString('sq-AL', { year: 'numeric', month: 'long', day: 'numeric' })}` : ''}.`
                : 'Ky konkurs nuk pranon aplikime për momentin.'}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{posting.title}</Typography>
          {posting.position && <Chip label={posting.position} sx={{ mt: 1 }} />}
        </Card>
      </Box>
    );
  }

  // ─── Success ───────────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9fafb', p: 2 }}>
        <Card elevation={2} sx={{ maxWidth: 500, width: '100%', textAlign: 'center', p: 4 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 60, color: 'success.main', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Aplikimi u dërgua me sukses!</Typography>
          <Typography color="text.secondary">
            Faleminderit <strong>{form.fullName}</strong>! Aplikimi juaj për pozitën
            {posting.position ? ` "${posting.position}"` : ''} u regjistrua. Do të njoftoheni nëse kualifikoheni.
          </Typography>
        </Card>
      </Box>
    );
  }

  const fmtDeadline = (d) => new Date(d).toLocaleDateString('sq-AL', { day: '2-digit', month: 'long', year: 'numeric' });
  const applyOpen = posting.isOpen !== false && !posting.isExpired && !posting.isUpcoming && posting.isActive !== false;
  const closedMsg = posting.isExpired ? 'Afati i aplikimit ka kaluar.'
    : posting.isUpcoming ? `Aplikimi hapet më ${posting.startDate ? fmtDeadline(posting.startDate) : 'së shpejti'}.`
    : (posting.isActive === false ? 'Ky konkurs është mbyllur.' : '');

  // Share the public posting link on social networks.
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = `Konkurs pune: ${posting.title}${posting.position ? ` — ${posting.position}` : ''}. Apliko këtu:`;
  const shareLinks = [
    { key: 'fb', label: 'Facebook', Icon: FacebookIcon, color: '#1877F2', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { key: 'li', label: 'LinkedIn', Icon: LinkedInIcon, color: '#0A66C2', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
    { key: 'wa', label: 'WhatsApp', Icon: WhatsAppIcon, color: '#25D366', href: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}` },
  ];
  const copyShareLink = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(shareUrl);
      else { const ta = document.createElement('textarea'); ta.value = shareUrl; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove(); }
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  // ─── Step 1: Job details + "Apliko" ────────────────────────────────────────
  if (step === 'details') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f0f4f8', py: 3, px: 2 }}>
        <Box sx={{ maxWidth: 720, mx: 'auto' }}>
          {/* Banner image (if the posting has one) */}
          {posting.image && (
            <Card elevation={0} sx={{ mb: 2, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
              <Box component="img" src={posting.image} alt={posting.title}
                sx={{ display: 'block', width: '100%', maxHeight: 280, objectFit: 'cover' }} />
            </Card>
          )}
          {/* Blue header */}
          <Card elevation={0} sx={{ mb: 2, borderRadius: 3, background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)', color: '#fff', px: { xs: 2.5, sm: 3.5 }, py: { xs: 2.5, sm: 3 } }}>
            <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 2, fontSize: '0.62rem' }}>Konkurs Pune</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5 }}>{posting.title}</Typography>
            <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1.5, flexWrap: 'wrap' }}>
              {posting.position && <Chip size="small" label={posting.position} sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: '#fff', fontWeight: 600 }} />}
              {posting.deadline && <Chip size="small" icon={<AccessTimeIcon sx={{ fontSize: 15, color: '#fff !important' }} />} label={`Afati: ${fmtDeadline(posting.deadline)}`} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />}
            </Stack>
          </Card>

          {/* Details + CTA */}
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              <Typography sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.68rem', color: 'text.secondary', mb: 1 }}>Përshkrimi i pozitës</Typography>
              {posting.description
                ? <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{posting.description}</Typography>
                : <Typography variant="body2" color="text.secondary">Nuk ka përshkrim shtesë për këtë konkurs.</Typography>}

              {!applyOpen && closedMsg && <Alert severity="warning" sx={{ mt: 2.5 }}>{closedMsg}</Alert>}

              <Button variant="contained" size="large" fullWidth disabled={!applyOpen} onClick={() => setStep('form')}
                sx={{ mt: 3, fontWeight: 700, py: 1.25, borderRadius: 2 }}>
                {applyOpen ? 'Apliko tani' : 'Aplikimet janë mbyllur'}
              </Button>
              {applyOpen && (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                  Do të plotësoni të dhënat tuaja në hapin tjetër.
                </Typography>
              )}

              {/* Share the posting */}
              <Divider sx={{ my: 2.5 }} />
              <Typography sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.66rem', color: 'text.secondary', mb: 1, textAlign: 'center' }}>
                Shpërndaje konkursin
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                {shareLinks.map((s) => (
                  <Button
                    key={s.key} component="a" href={s.href} target="_blank" rel="noopener noreferrer"
                    variant="outlined" size="small" startIcon={<s.Icon />}
                    sx={{ textTransform: 'none', borderRadius: 2, color: s.color, borderColor: 'divider', '&:hover': { borderColor: s.color, bgcolor: `${s.color}0F` } }}
                  >
                    {s.label}
                  </Button>
                ))}
                <Button
                  onClick={copyShareLink}
                  variant="outlined" size="small" startIcon={copied ? <CheckIcon /> : <LinkIcon />}
                  sx={{ textTransform: 'none', borderRadius: 2, color: copied ? 'success.main' : 'text.secondary', borderColor: 'divider', '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' } }}
                >
                  {copied ? 'U kopjua!' : 'Kopjo linkun'}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
            Të dhënat tuaja do të trajtohen me konfidencialitet sipas politikës tonë të privatësisë.
          </Typography>
        </Box>
      </Box>
    );
  }

  // ─── Step 2: Apply Form ────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f4f8', py: 3, px: 2 }}>
      <Box sx={{ maxWidth: 920, mx: 'auto' }}>
        {/* Header */}
        <Card elevation={0} sx={{ mb: 2, borderRadius: 3, background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)', color: '#fff', px: 3, py: 2 }}>
          <Button onClick={() => setStep('details')} startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            sx={{ color: '#fff', textTransform: 'none', fontWeight: 600, mb: 0.5, ml: -0.5, opacity: 0.9, '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}>
            Detajet e konkursit
          </Button>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>{posting.title}</Typography>
            {posting.position && (
              <Chip size="small" label={posting.position} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }} />
            )}
            {posting.deadline && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ opacity: 0.9 }}>
                <AccessTimeIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">Afati: <strong>{fmtDeadline(posting.deadline)}</strong></Typography>
              </Stack>
            )}
          </Stack>
        </Card>

        {/* Form */}
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" sx={{ mb: 1.5, gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Të dhënat tuaja</Typography>
              <Typography variant="caption" color="text.secondary">
                Fushat me <span style={{ color: '#d32f2f' }}>*</span> janë të detyrueshme.
              </Typography>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={1.5}>
                {/* Personal */}
                <Grid item xs={12}>
                  <Typography sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.66rem', color: 'text.secondary' }}>Të dhëna personale</Typography>
                  <Divider sx={{ mt: 0.5 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField size="small" label="Emri i plotë *" fullWidth value={form.fullName} onChange={set('fullName')}
                    error={!!errors.fullName} helperText={errors.fullName} placeholder="p.sh. Arben Gashi" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField size="small" label="Email *" type="email" fullWidth value={form.email} onChange={set('email')}
                    error={!!errors.email} helperText={errors.email} placeholder="email@shembull.com" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField size="small" label="Telefoni *" fullWidth value={form.phone} onChange={set('phone')}
                    error={!!errors.phone} helperText={errors.phone} placeholder="+383 49 000 000" />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField size="small" label="Datëlindja" type="date" fullWidth value={form.dateOfBirth}
                    onChange={set('dateOfBirth')} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField size="small" label="Qyteti" fullWidth value={form.city} onChange={set('city')} placeholder="p.sh. Prishtinë" />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField size="small" label="Adresa" fullWidth value={form.address} onChange={set('address')} placeholder="Rruga, nr. shtëpisë..." />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField size="small" label="Rekomanduar nga" fullWidth value={form.referredBy} onChange={set('referredBy')}
                    placeholder="Kush ju rekomandoi (opsionale)" />
                </Grid>

                {/* Qualifications */}
                <Grid item xs={12} sx={{ mt: 0.75 }}>
                  <Typography sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.66rem', color: 'text.secondary' }}>Kualifikimet</Typography>
                  <Divider sx={{ mt: 0.5 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField size="small" label="Arsimimi" fullWidth multiline rows={2} value={form.education}
                    onChange={set('education')} placeholder="p.sh. Bachelor në Informatikë, UP, 2019" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField size="small" label="Eksperienca e punës" fullWidth multiline rows={2} value={form.experience}
                    onChange={set('experience')} placeholder="Përshkruani eksperiencën relevante..." />
                </Grid>
                <Grid item xs={12}>
                  <TextField size="small" label="Aftësitë (Skills)" fullWidth value={form.skills} onChange={set('skills')}
                    placeholder="p.sh. JavaScript, React, komunikim, punë ekipore..." />
                </Grid>

                {/* Links + cover letter */}
                <Grid item xs={12} sx={{ mt: 0.75 }}>
                  <Typography sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.66rem', color: 'text.secondary' }}>Linqe & Motivimi (opsionale)</Typography>
                  <Divider sx={{ mt: 0.5 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField size="small" label="LinkedIn URL" fullWidth value={form.linkedinUrl} onChange={set('linkedinUrl')} placeholder="https://linkedin.com/in/username" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField size="small" label="Portfolio / Website" fullWidth value={form.portfolioUrl} onChange={set('portfolioUrl')} placeholder="https://portfolio-im.com" />
                </Grid>
                <Grid item xs={12}>
                  <TextField size="small" label="Letra motivuese — Pse jeni i/e interesuar?" fullWidth multiline rows={3}
                    value={form.notes} onChange={set('notes')} placeholder="Shkruani motivimin tuaj..." />
                </Grid>

                {/* Documents (CV, certifikata) */}
                <Grid item xs={12} sx={{ mt: 0.75 }}>
                  <Typography sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.66rem', color: 'text.secondary' }}>Dokumenta (CV, Certifikata)</Typography>
                  <Divider sx={{ mt: 0.5 }} />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    onClick={() => document.getElementById('public-apply-docs').click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); addDocuments(e.dataTransfer.files); }}
                    sx={{
                      border: '2px dashed', borderColor: 'divider', borderRadius: 2,
                      px: 2, py: 1.5, textAlign: 'center', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25, flexWrap: 'wrap',
                      transition: 'border-color 0.15s, background-color 0.15s',
                      '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                    }}
                  >
                    <input
                      id="public-apply-docs"
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      onChange={(e) => { addDocuments(e.target.files); e.target.value = ''; }}
                    />
                    <UploadFileIcon sx={{ fontSize: 26, color: 'primary.main' }} />
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Kliko për të ngarkuar CV-në ose tërhiq skedarët këtu
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        PDF, DOC, DOCX, JPG, PNG · maks 3 skedarë · deri 10MB secili
                      </Typography>
                    </Box>
                  </Box>
                  {documents.length > 0 && (
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      {documents.map((f, idx) => (
                        <Stack key={`${f.name}-${idx}`} direction="row" alignItems="center" spacing={1}
                          sx={{ px: 1.25, py: 0.5, borderRadius: 1.5, bgcolor: 'action.hover' }}>
                          <InsertDriveFileIcon sx={{ fontSize: 17, color: 'primary.main' }} />
                          <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>{f.name}</Typography>
                          <Typography variant="caption" color="text.disabled">
                            {(f.size / 1024 / 1024).toFixed(1)} MB
                          </Typography>
                          <CloseIcon
                            sx={{ fontSize: 16, color: 'text.disabled', cursor: 'pointer', '&:hover': { color: 'error.main' } }}
                            onClick={(e) => { e.stopPropagation(); setDocuments(prev => prev.filter((_, i) => i !== idx)); }}
                          />
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Grid>

                {/* Consent / data-retention declaration */}
                <Grid item xs={12} sx={{ mt: 0.75 }}>
                  <Box sx={{ px: 1.5, py: 1.25, borderRadius: 2, border: '1px solid', borderColor: errors.consent ? 'error.main' : 'divider', bgcolor: 'action.hover' }}>
                    <FormControlLabel
                      sx={{ alignItems: 'flex-start', m: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={form.consent}
                          onChange={(e) => { setForm(f => ({ ...f, consent: e.target.checked })); if (errors.consent) setErrors(x => ({ ...x, consent: undefined })); }}
                          sx={{ pt: 0.25 }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                          Deklaroj se jam dakord që të dhënat e mia personale të parashtruara në këtë aplikim të ruhen dhe përpunohen për
                          qëllime rekrutimi dhe punësimi, dhe pajtohem që kompania ka të drejtë t'i ruajë këto të dhëna në përputhje me
                          politikën e privatësisë dhe legjislacionin për mbrojtjen e të dhënave personale. <span style={{ color: '#d32f2f' }}>*</span>
                        </Typography>
                      }
                    />
                  </Box>
                  {errors.consent && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>{errors.consent}</Typography>
                  )}
                </Grid>

                {/* Pëlqimi për mundësi të ardhshme — veçmas, dhe pa yll: aplikimi
                    dërgohet edhe pa të. Pa këtë kuti, kompania nuk ka si ta ftojë
                    këtë person për një pozitë tjetër vite më vonë, dhe kjo është
                    pikërisht zgjedhja e personit, jo e sistemit. */}
                <Grid item xs={12} sx={{ mt: 0.75 }}>
                  <Box sx={{ px: 1.5, py: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <FormControlLabel
                      sx={{ alignItems: 'flex-start', m: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={form.reuseConsent}
                          onChange={(e) => setForm(f => ({ ...f, reuseConsent: e.target.checked }))}
                          sx={{ pt: 0.25 }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                          Dëshiroj që të dhënat e mia të mbahen edhe për mundësi të ardhshme punësimi, dhe
                          pranoj të kontaktohem nëse hapet një pozitë e përshtatshme. (Opsionale — mund ta
                          tërhiqni këtë pëlqim në çdo kohë.)
                        </Typography>
                      }
                    />
                  </Box>
                </Grid>
                {/* Submit */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={submitting || !form.consent}
                    sx={{ fontWeight: 700, py: 1.15, mt: 0.5, borderRadius: 2 }}
                  >
                    {submitting ? <CircularProgress size={22} color="inherit" /> : 'Dërgo Aplikimin'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>

        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
          Të dhënat tuaja do të trajtohen me konfidencialitet sipas politikës tonë të privatësisë.
        </Typography>
      </Box>
    </Box>
  );
}
